import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useApp } from "@/context/AppContext";
import { farmerLogin } from "@/lib/api";
import { toast } from "sonner";
import { Sprout, Loader2 } from "lucide-react";

const LoginDialog = ({ open, onOpenChange, onSuccess }) => {
  const { t, setFarmer, farmer } = useApp();
  const [form, setForm] = useState({
    mobile: farmer?.mobile || "",
    name: farmer?.name || "",
    address: farmer?.address || "",
    village: farmer?.village || "",
    pincode: farmer?.pincode || "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(form.mobile.trim())) {
      toast.error("Please enter valid 10-digit mobile number");
      return;
    }
    if (!form.name.trim() || !form.address.trim()) {
      toast.error("Name and address are required");
      return;
    }
    setLoading(true);
    try {
      const res = await farmerLogin(form);
      setFarmer(res);
      toast.success(`Welcome, ${res.name}!`);
      onOpenChange(false);
      if (onSuccess) onSuccess(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#E2E8F0] max-w-md" data-testid="login-dialog">
        <DialogHeader>
          <div className="w-14 h-14 rounded-2xl bg-[#2D5A27] flex items-center justify-center mb-3">
            <Sprout className="w-7 h-7 text-white" />
          </div>
          <DialogTitle className="font-heading text-2xl">{t("loginTitle")}</DialogTitle>
          <DialogDescription>{t("loginSubtitle")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="mobile" className="text-sm font-medium">{t("mobileNumber")}</Label>
            <Input
              id="mobile"
              data-testid="login-mobile-input"
              value={form.mobile}
              onChange={update("mobile")}
              placeholder="9876543210"
              maxLength={10}
              className="mt-1.5 h-12 bg-white"
              required
            />
          </div>
          <div>
            <Label htmlFor="name" className="text-sm font-medium">{t("fullName")}</Label>
            <Input
              id="name"
              data-testid="login-name-input"
              value={form.name}
              onChange={update("name")}
              className="mt-1.5 h-12 bg-white"
              required
            />
          </div>
          <div>
            <Label htmlFor="address" className="text-sm font-medium">{t("address")}</Label>
            <Input
              id="address"
              data-testid="login-address-input"
              value={form.address}
              onChange={update("address")}
              className="mt-1.5 h-12 bg-white"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="village" className="text-sm font-medium">{t("village")}</Label>
              <Input
                id="village"
                data-testid="login-village-input"
                value={form.village}
                onChange={update("village")}
                className="mt-1.5 h-12 bg-white"
              />
            </div>
            <div>
              <Label htmlFor="pincode" className="text-sm font-medium">{t("pincode")}</Label>
              <Input
                id="pincode"
                data-testid="login-pincode-input"
                value={form.pincode}
                onChange={update("pincode")}
                maxLength={6}
                className="mt-1.5 h-12 bg-white"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full h-12 rounded-full bg-[#2D5A27] hover:bg-[#21421D] text-white font-semibold pressable"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("loginBtn")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LoginDialog;
